import axios from "axios";

/**
 * SuperadminService - API service for superadmin functionality
 * Handles all superadmin-related API calls including user management, clinic management, and system statistics
 */
class SuperadminService {
  /**
   * Check superadmin health and authentication
   * @returns {Promise} API response
   */
  static async checkHealth() {
    return axios.get('/api/superadmin/health');
  }

  /**
   * Get system statistics
   * @returns {Promise} API response with system stats
   */
  static async getSystemStats() {
    return axios.get('/api/superadmin/stats');
  }

  // USER MANAGEMENT METHODS

  /**
   * Search users with advanced filtering
   * @param {Object} params - Search parameters
   * @param {string} params.firstName - Search by first name
   * @param {string} params.lastName - Search by last name  
   * @param {string} params.email - Search by email
   * @param {string} params.mobile - Search by phone number
   * @param {string} params.licenseNumber - Search by doctor license
   * @param {number} params.userTypeId - Filter by user type (1=doctor, 2=staff)
   * @param {number} params.statusId - Filter by status (0=inactive, 1=active)
   * @param {string} params.clinicCode - Filter users from specific clinic
   * @param {string} params.branchCode - Filter users from specific branch
   * @param {boolean} params.hasLicense - Filter doctors with/without license
   * @param {string} params.sortBy - Sort field: creation_dt, email, first_name_th, dr_license_no
   * @param {string} params.sortOrder - Sort direction: ASC, DESC
   * @param {number} params.limit - Results per page (default: 20)
   * @param {number} params.offset - Pagination offset (default: 0)
   * @returns {Promise} API response with user search results
   */
  static async searchUsers(params = {}) {
    return axios.get('/api/superadmin/users/search', { params });
  }

  /**
   * Get detailed information about a specific user
   * @param {number} userId - User ID
   * @returns {Promise} API response with user details
   */
  static async getUserDetails(userId) {
    return axios.get(`/api/superadmin/users/${userId}`);
  }

  /**
   * Update user status (activate/deactivate)
   * @param {number} userId - User ID
   * @param {Object} statusData - Status update data
   * @param {number} statusData.statusId - New status (0=inactive, 1=active)
   * @param {string} statusData.reason - Optional reason for status change
   * @returns {Promise} API response
   */
  static async updateUserStatus(userId, statusData) {
    return axios.put(`/api/superadmin/users/${userId}/status`, statusData);
  }

  // CLINIC MANAGEMENT METHODS

  /**
   * Search clinics with advanced filtering
   * @param {Object} params - Search parameters
   * @param {string} params.clinicName - Search by clinic name (Thai/English)
   * @param {string} params.address - Search by clinic or branch address
   * @param {string} params.telephone - Search by clinic or branch phone
   * @param {string} params.branchName - Search by branch name (Thai/English)
   * @param {string} params.taxId - Search by tax ID
   * @param {boolean} params.hasActiveBranches - Filter clinics with active branches
   * @param {number} params.minMembers - Minimum number of members
   * @param {number} params.maxMembers - Maximum number of members
   * @param {string} params.sortBy - Sort field: creation_dt, clinic_name_th, total_members, total_branches
   * @param {string} params.sortOrder - Sort direction: ASC, DESC
   * @param {number} params.limit - Results per page (default: 20)
   * @param {number} params.offset - Pagination offset (default: 0)
   * @returns {Promise} API response with clinic search results
   */
  static async searchClinics(params = {}) {
    return axios.get('/api/superadmin/clinics/search', { params });
  }

  /**
   * Get detailed information about a specific clinic
   * @param {string} clinicCode - Clinic code
   * @returns {Promise} API response with clinic details
   */
  static async getClinicDetails(clinicCode) {
    return axios.get(`/api/superadmin/clinics/${clinicCode}`);
  }

  // ADS MANAGEMENT METHODS

  /**
   * Increment ads version to force update advertisements
   * @returns {Promise} API response with version update details
   */
  static async incrementAdsVersion() {
    return axios.post('/api/superadmin/config/ads-version/increment');
  }

  // UTILITY METHODS

  /**
   * Build query parameters object, removing empty/null values
   * @param {Object} params - Raw parameters
   * @returns {Object} Cleaned parameters object
   */
  static cleanParams(params) {
    const cleaned = {};
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (value !== null && value !== undefined && value !== '') {
        cleaned[key] = value;
      }
    });
    return cleaned;
  }

  /**
   * Build search URL with pagination
   * @param {string} baseUrl - Base API URL
   * @param {Object} params - Search parameters
   * @param {number} page - Current page (1-based)
   * @param {number} limit - Items per page
   * @returns {Object} URL parameters with pagination
   */
  static buildPaginatedParams(params, page = 1, limit = 20) {
    return {
      ...this.cleanParams(params),
      limit,
      offset: (page - 1) * limit
    };
  }
}

export default SuperadminService;